/**
 * The Complete step's behavior half: the shared flow (`useCompleteStep`) on the website's
 * session, and where each ending goes: the website's pay page once only payment is left, its
 * booked page when everything is in, and the booking's page when the studio reviews the forms
 * first or the booking no longer waits. Back returns where the client came from on this site, or
 * the booking's page. The step's lines come from the shared parts the app's step reads. Draws
 * nothing.
 *
 * The standard website draws the app's step (`CompleteStepScreen` on a phone, `CompleteStepColumn`
 * on a computer) over the same flow, through `screen`.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { completeStepLines } from '@/pages/CompleteBookingPage/completeStepParts';
import type { CompleteStepScreenProps } from '@/pages/CompleteBookingPage/CompleteStepScreen';
import { useCompleteStep, type CompleteFetcher } from '@/pages/CompleteBookingPage/useCompleteStep';
import type { FieldError, FormDefinition, FormResponseInput } from '@/types/forms';
import { formatFinishBy } from '@/utils/waitingBooking';

import { SiteFetchError } from '../../api/site';
import { webFetch } from '../../api/session';
import { usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { keepTimes } from '../../format';
import { accountKeys } from '../../hooks/useWebAccount';
import { useWebBooking } from '../../hooks/useWebBooking';
import { usePageMover } from '../../pageMover';
import { contract, type CompleteStepActions, type CompleteStepEvents, type CompleteStepState } from './contract';

export interface CompleteStepInput extends PieceInput<CompleteStepEvents> {
  readonly bookingId: string;
}

export type CompleteStepRuntime = PieceRuntime<typeof contract, CompleteStepState, CompleteStepActions> & {
  /** The form drawn, once read. */
  readonly form: FormDefinition | null;
  readonly serverErrors: FieldError[];
  /** The same flow as the app's step screen and column read it (the standard website). */
  readonly screen: CompleteStepScreenProps;
};

/** The website session's transport, failing as the step reads a failed answer: status, code, the answer's body. */
const completeFetch: CompleteFetcher = async (path, init) => {
  try {
    return await webFetch(path, init);
  } catch (err) {
    if (err instanceof SiteFetchError) throw { status: err.status, code: err.code, details: err.body };
    throw err;
  }
};

export function useCompleteStepPiece(input: CompleteStepInput): CompleteStepRuntime {
  const { bookingId } = input;
  const options = usePieceOptions(contract, input.options);
  const navigate = usePageMover();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { detail } = useWebBooking(bookingId);
  const booking = detail.data;
  const bookingPath = `/account/bookings/${encodeURIComponent(bookingId)}`;

  const leaveTo = useCallback(
    (path: string) => {
      void queryClient.invalidateQueries({ queryKey: accountKeys.booking(bookingId) });
      void queryClient.invalidateQueries({ queryKey: accountKeys.dashboard });
      navigate(path, { replace: true });
    },
    [bookingId, navigate, queryClient],
  );

  const flow = useCompleteStep({
    bookingId,
    enabled: Boolean(bookingId),
    fetcher: completeFetch,
    onPay: () => leaveTo(`${bookingPath}/pay`),
    onDone: () => leaveTo(`/booked/${encodeURIComponent(bookingId)}`),
    onPendingApproval: () => leaveTo(bookingPath),
  });

  const back = useCallback(
    () => (location.key === 'default' ? navigate(bookingPath, { replace: true }) : navigate(-1)),
    [bookingPath, location.key, navigate],
  );

  const { submit, retry, privacy } = flow;
  const actions = useMemo<CompleteStepActions>(
    () => ({
      submit: (responses) => submit(responses as FormResponseInput[]),
      setConsent: privacy.onChange,
      retry,
      back,
    }),
    [back, privacy.onChange, retry, submit],
  );

  const serviceName = booking?.service.name ?? null;
  const owed = booking?.outstanding;
  const step = flow.step;
  const lines = step ? completeStepLines(step, owed) : null;

  return {
    state: {
      status: flow.state,
      title: serviceName ?? step?.form.title ?? null,
      step: lines?.typeLine ?? null,
      description: step?.form.description || null,
      finishBy: flow.completeBy ? keepTimes(formatFinishBy(flow.completeBy)) : null,
      lastForm: lines?.isLastForm ?? false,
      paymentAhead: lines?.hasPaymentAhead ?? false,
      consentRequired: privacy.required,
      consented: privacy.ticked,
      consentError: privacy.error,
    },
    actions,
    options,
    form: step?.form ?? null,
    serverErrors: flow.serverErrors,
    screen: {
      status: flow.state,
      step,
      completeBy: flow.completeBy,
      serverErrors: flow.serverErrors,
      privacy,
      serviceName,
      owed,
      onSubmit: submit,
      onRetry: retry,
      onBack: back,
    },
  };
}
